import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import { useAuthStore } from '@/stores/authStore';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { GlobalUploadProgress } from '@/components/uploads/GlobalUploadProgress';
import { processSyncQueue } from '@/lib/sync';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import Properties from '@/pages/Properties';
import PropertyDetail from '@/pages/PropertyDetail';
import Dashboard from '@/pages/Dashboard';
import Calculator from '@/pages/Calculator';
import Settings from '@/pages/Settings';
import ShareView from '@/pages/ShareView';

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export default function App() {
  const { initialize, user } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (user && navigator.onLine) {
      processSyncQueue();
    }
  }, [user]);

  return (
    <ErrorBoundary>
      <Toaster position="top-right" richColors />
      <GlobalUploadProgress />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public */}
          <Route
            path="/auth/login"
            element={user ? <Navigate to="/" replace /> : <Login />}
          />
          <Route
            path="/auth/register"
            element={user ? <Navigate to="/" replace /> : <Register />}
          />

          {/* Protected + Layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Properties />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PropertyDetail />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calculator"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Calculator />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Settings />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Public Share Links - kein Auth nötig */}
          <Route path="/s/:token" element={<ShareView />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </ErrorBoundary>
  );
}